import axios from 'axios';
import { OnboardQuestion } from './components/SoloOnboarding/state-machine';
import { AIReview, ActivityContextQuestionAnswer } from './contexts/activity';
import { QUESTION_TYPE } from './components/Activity/Question';
import {
  getPreviewActivityRoute,
  type GetPreviewActivityParams,
  type GetPreviewActivityResponse,
} from '@enki/contracts/preview/get-activity';
import {
  getPreviewStatsRoute,
  type GetPreviewStatsParams,
} from '@enki/contracts/preview/get-stats';
import { requestContract } from './api/contract-client';
import { unsubscribeProsumerRoute } from '@enki/contracts/prosumer/public-token';
import {
  passwordlessStartRoute,
  passwordlessVerifyRoute,
} from '@enki/contracts/common/auth';
import {
  getHealthcheckRoute,
  getProjectExamplesRoute,
  getStreakStatsRoute,
} from '@enki/contracts/common/product-reads';
import type { GetProjectExamplesResponse } from '@enki/contracts/common/product-reads';
import { submitTeamsActivityRoute } from '@enki/contracts/teams/writes';
import { submitTeamsAnswerRoute } from '@enki/contracts/teams/writes';
import { checkProsumerEmailAvailabilityRoute } from '@enki/contracts/prosumer/reads';
import { joinProsumerContentRoute as joinProsumerContentContractRoute } from '@enki/contracts/prosumer/onboarding';
import {
  submitProsumerActivityRoute,
  createProsumerAIReviewRoute,
  submitProsumerAnswerRoute,
} from '@enki/contracts/prosumer/submissions';
import {
  postTeamsInterestFormRoute,
  createTeamsAIReviewRoute,
} from '@enki/contracts/teams/learner-writes';

export type ActivityPayload = {
  id: number;
  updated_at: string;
  created_at: string;
  is_core: boolean | null;
  title: string;
  summary: string | null;
  external_url: string | null;
  type: 'EXTERNAL' | 'EXERCISE'; // TODO: could probably remove as no content has type 'EXTERNAL'
  mission_id: number | null;
  estimate_seconds: number;
  index_in_mission: number;
  exercise_id: number | null;
  is_teams_only: boolean;
  slug: string;
} & {
  activity_session_id: number;
  activity_session_instructor_review: string | null;
  activity_session_instructor_score: number | null;
  activity_session_instructor_question_comments: string[];
} & {
  latest_submission: LatestSubmissionPayload;
  exercise: {
    id: number;
    updated_at: string;
    created_at: string;
    description: string;
    lesson: string;
    difficulty: number;
    activity_id: number | null;
    slug: string;
  } & {
    questions: Array<QuestionPayload>;
  };
  content_id: number;
  content_slug: string;
  content_title: string;
  next_activity_slug: string | null;
  next_mission_slug: string | null;
};
export type LatestSubmissionPayload = null | {
  id: number;
  updated_at: string;
  created_at: string;
  difficulty_rating: number | null;
  feedback: string | null;
  seconds_spent: number | null;
  activity_session_id: number;
  ai_review: AIReview | null;
  answer_states: Record<string, AnswerState> | null;
  mentor_reviews:
    | {
        questionId: number;
        isRequested: boolean;
        review: string | null;
        comment: string | null;
      }[]
    | null;
};
export type QuestionPayload = {
  id: number;
  updated_at: string;
  created_at: string;
  type: QUESTION_TYPE;
  text: string;
  hints: Array<string> | null;
  exercise_id: number | null;
  slug: string;
  setup_sql: string | null; // creationDDL + creationDML
  answer_dql: string | null; // for checking dql answers
  verification_sql: string | null; // for checking ddl + dml answers
  index_in_exercise: number;
  looker_link: string | null;
  tableau_link: string | null;
  google_sheets_link: string | null;
  model_answer: string | null;
  notes: string | null;
  powerbi_csv: string | null;
  initial_py: string | null;
  output_regexes_py: Array<string> | null;
  input_regexes_py: Array<string> | null;
  expect_error_py: boolean | null;
  intro_lesson_text: string;
  setup_py: string | null;
  llm_initial_prompt: string | null;
  multiple_choice_answers: {
    correct_answer: string;
    distractors: Array<string>;
  } | null;
  strict_text_answers: Array<string> | null;
};

export type UserPayload = {
  id: number;
  created_at: string;
  updated_at: string;
  email: string | null;
  company: string | null;
  name: string | null;
  auth0_ids: string[];
  roles: Array<string>;
  slack_member_id: number | null;
  has_ai_access: boolean;
  isSignUp: boolean;
  isAppUserOnly: boolean;
};

export type ActivitySubmission = {
  difficulty_rating: number | null;
  feedback: string | null;
  activity_session_id: number;
  seconds_spent: number;
  activity_id: number;
  answer_states: Record<string, AnswerState>;
};

export type AnswerState = {
  value: string;
  is_correct: boolean | null;
  is_submitted: boolean;
  submitted_at: Date | null;
  failed_correctness_checks?: string[];
  working_code?: string;
  result?: string | null;
  review?: string | null;
  does_satisfy_all_requirements?: boolean | null;
};

export type DashboardPayload = {
  missions: Array<{
    slug: string;
    title: string;
    index: number;
    sentAt: Date | null;
    content: {
      id: number;
      slug: string;
      title: string;
    };
    week: {
      id: number;
      slug: string;
      title: string;
      index: number;
    };
    activities: Array<{
      slug: string;
      isCore: boolean;
      title: string;
      instructorReview: string | null;
      instructorScore: number | null;
      isSubmitted: boolean;
      isCompleted: boolean;
      isSent: boolean;
    }>;
  }>;
  links: Array<{
    title: string;
    path: string;
  }>;
};

export type AppliedProjectTag = {
  value: string;
  type: 'TOPIC' | 'ROLE' | 'APPLICATION';
};

export type AppliedProjectStat = {
  slug: string;
  name: string;
  description: string;
  image_url: string;
  topic: string;
  is_submitted: boolean;
  is_finished: boolean;
  is_joined?: boolean;
  question_count: number;
  completedQuestions: number | null;
  tags: Array<AppliedProjectTag>;
  isLocked: boolean;
};

export type ActiveTeamsTrack = {
  id: number;
  name: string;
  slug: string;
  contentTitle: string;
  contentSlug: string;
  contentId: number;
  topic: string;
  lessonsCount: number;
  coreLessonsCount: number;
  completedLessonsCount: number;
  coreCompletedLessonsCount: number;
  attemptedLessonsCount: number;
  coreAttemptedLessonsCount: number;
  startedAt: Date;
  endedAt: Date;
  imageUrl: string;
  description: string;
};

export type OnboardingTrack = ActiveTeamsTrack & {
  onboarding: {
    survey: Array<{
      name: string;
      questions: Array<{
        id: string;
        title: string;
        subtitle?: string;
        type:
          | 'input'
          | 'multi-select'
          | 'single-select'
          | 'input-time-timezone';
        options?: string[];
      }>;
      isCompleted: boolean;
    }>;
    slack: {
      isCompleted: boolean;
    };
  };
};

export type LearningPlan = Array<
  | {
      type: 'module';
      slug: string;
      reasoning: string;
    }
  | {
      type: 'project';
      slug: string;
      reasoning: string;
    }
>;

export type PlanExplanationPayload = {
  summary: string;
  milestones: string[];
};

export type ProsumerProfilePayload = {
  hasProfile: boolean;
  profile: {
    user_id: number;
    pro_until: Date | null;
    utc_notification_time: string | null;
    onboarding_steps: Array<Record<string, unknown>> | null;
    onboarding_content_id: number | null;
    learning_plan: LearningPlan | null;
  };
  learnMode: {
    hasAccess: boolean;
    customerPortalUrl: string;
    subscriptionId?: string;
    willRenew: boolean;
    cycleEndDate: Date;
    shouldGetTrial: boolean;
  };
  contentModules: Array<ProsumerContentModule>;
};

export type ProsumerContentModule = {
  prosumerContentSessionId: number;
  contentId: number;
  contentTitle: string;
  contentSlug: string;
  contentImageUrl: string;
  contentDescription: string;
  contentTopic: string;
  isJoined: boolean;
  joinedAt: Date;
  nextMissionSlug: string;
  nextActivitySlug: string;
  nextActivityName: string;
  hasSubmission: boolean;
  lessonsCount: number;
  completedLessonsCount: number;
  isFinished: boolean;
};

type StreakData = {
  streakDays: number;
  maxStreak: number;
  exercises: number;
  aiMessages: number;
  dailyStats: {
    day: string;
    status: 'complete' | 'passed' | 'today';
  }[];
};

export const websoketURL = (() => {
  switch (process.env.REACT_APP_ENV) {
    case 'development':
      return 'ws://localhost:1235';
    case 'staging':
      return 'wss://staging.desktop-ws.enki.com';
    case 'production':
      return 'wss://desktop-ws.enki.com';
    default:
      return 'ws://localhost:1235';
  }
})();

export async function getPreviewActivity(
  token: string,
  // Params come from the contract the backend validates against.
  { missionSlug, activitySlug }: GetPreviewActivityParams
): Promise<ActivityPayload> {
  const response = await requestContract(getPreviewActivityRoute, {
    token,
    params: { missionSlug, activitySlug },
  });

  // Preview rows have no learner session. Preserve the established negative
  // sentinel used by preview-only ActivityPayloads, and translate the shared
  // string union to the legacy frontend enum at this boundary.
  return {
    ...response,
    activity_session_id: -1,
    exercise: {
      ...response.exercise,
      questions: response.exercise.questions.map(toLegacyPreviewQuestion),
    },
  };
}

type PreviewQuestionType =
  GetPreviewActivityResponse['exercise']['questions'][number]['type'];

const previewQuestionTypes: Record<PreviewQuestionType, QUESTION_TYPE> = {
  FREE_TEXT: QUESTION_TYPE.FREE_TEXT,
  FREE_TEXT_PLAYGROUND: QUESTION_TYPE.FREE_TEXT_SQL_PLAYGROUND,
  PLAYGROUND: QUESTION_TYPE.SQL_PLAYGROUND,
  LOOKER_EXTERNAL: QUESTION_TYPE.LOOKER_EXTERNAL,
  FREE_TEXT_LOOKER_EMBED: QUESTION_TYPE.FREE_TEXT_LOOKER_EMBED,
  TABLEAU_EXTERNAL: QUESTION_TYPE.TABLEAU_EXTERNAL,
  FREE_TEXT_TABLEAU_EMBED: QUESTION_TYPE.FREE_TEXT_TABLEAU_EMBED,
  GOOGLE_SHEETS_EXTERNAL: QUESTION_TYPE.SHEETS_EXTERNAL,
  FREE_TEXT_GOOGLE_SHEETS_EXTERNAL: QUESTION_TYPE.FREE_TEXT_SHEETS_EMBED,
  FREE_TEXT_POWERBI_EXTERNAL: QUESTION_TYPE.FREE_TEXT_POWERBI_EXTERNAL,
  FREE_TEXT_SQL_CODE: QUESTION_TYPE.FREE_TEXT_SQL_CODE,
  PYTHON_PLAYGROUND: QUESTION_TYPE.PYTHON_PLAYGROUND,
  PYTHON_MULTIPLE_CHOICE: QUESTION_TYPE.PYTHON_MULTIPLE_CHOICE,
  SQL_MULTIPLE_CHOICE: QUESTION_TYPE.SQL_MULTIPLE_CHOICE,
  LLM_PLAYGROUND: QUESTION_TYPE.LLM_PLAYGROUND,
  STRICT_TEXT: QUESTION_TYPE.STRICT_TEXT,
  FREE_TEXT_AI_VERIFIED: QUESTION_TYPE.FREE_TEXT_AI_VERIFIED,
  FREE_TEXT_SELF_CHECK: QUESTION_TYPE.FREE_TEXT_SELF_CHECK,
};

export function toLegacyPreviewQuestion<
  T extends { type: PreviewQuestionType },
>(question: T): Omit<T, 'type'> & { type: QUESTION_TYPE } {
  return {
    ...question,
    type: previewQuestionTypes[question.type],
  };
}

export type PasswordlessTokensPayload = {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  tokenType: string;
};

export async function startPasswordless({
  email,
  turnstileToken,
}: {
  email: string;
  turnstileToken: string;
}): Promise<void> {
  await requestContract(passwordlessStartRoute, {
    body: { email, turnstileToken },
  });
}

export async function verifyPasswordlessCode({
  email,
  code,
}: {
  email: string;
  code: string;
}): Promise<PasswordlessTokensPayload> {
  return requestContract(passwordlessVerifyRoute, {
    body: { email, code },
  });
}

export async function getProsumerActivity(
  token: string,
  { missionSlug, activitySlug }: { missionSlug: string; activitySlug: string }
): Promise<ActivityPayload> {
  const response = await axios<null, ActivityPayload>({
    method: 'get',
    url: `/prosumer/missions/${missionSlug}/activities/${activitySlug}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function getPreviewDashboard(
  token: string,
  { contentSlug }: GetPreviewStatsParams
): Promise<DashboardPayload> {
  return requestContract(getPreviewStatsRoute, {
    token,
    params: { contentSlug },
  });
}

export async function getProsumerDashboard(
  token: string,
  { contentSlug }: { contentSlug: string }
): Promise<DashboardPayload> {
  const response = await axios<null, DashboardPayload>({
    method: 'get',
    url: `/prosumer/${contentSlug}/stats`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function saveTeamsActivity(
  token: string,
  {
    trackSlug,
    submission,
  }: { trackSlug: string; submission: ActivitySubmission }
): Promise<void> {
  await requestContract(submitTeamsActivityRoute, {
    token,
    params: { trackSlug },
    body: {
      ...submission,
      answer_states: Object.fromEntries(
        Object.entries(submission.answer_states).map(([slug, answerState]) => [
          slug,
          {
            ...answerState,
            submitted_at: answerState.submitted_at?.toISOString() ?? null,
          },
        ])
      ),
    },
  });
  return;
}

export async function saveProsumerActivity(
  token: string,
  {
    contentSlug,
    submission,
  }: { contentSlug: string; submission: ActivitySubmission }
): Promise<void> {
  await requestContract(submitProsumerActivityRoute, {
    token,
    params: { contentSlug },
    body: {
      ...submission,
      answer_states: Object.fromEntries(
        Object.entries(submission.answer_states).map(([slug, answerState]) => [
          slug,
          {
            ...answerState,
            submitted_at: answerState.submitted_at?.toISOString() ?? null,
          },
        ])
      ),
    },
  });
  return;
}

export async function verifyAuthUser(token: string): Promise<UserPayload> {
  const response = await axios.get<UserPayload>('/verify-user', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function getStreakStats(token: string): Promise<StreakData> {
  return requestContract(getStreakStatsRoute, { token }) as Promise<StreakData>;
}

export async function validateProsumerAnswerAttempt(
  token: string,
  {
    value,
    submissionId,
    questionSlug,
  }: {
    value: string;
    submissionId: number;
    questionSlug: string;
  }
): Promise<void> {
  await requestContract(submitProsumerAnswerRoute, {
    token,
    timeout: 1000 * 30,
    body: {
      value,
      submissionId,
      questionSlug,
    },
  });
}

export async function validateTeamsAnswerAttempt(
  token: string,
  {
    value,
    activitySessionId,
    questionSlug,
  }: {
    value: string;
    activitySessionId: number;
    questionSlug: string;
  }
): Promise<void> {
  await requestContract(submitTeamsAnswerRoute, {
    token,
    timeout: 1000 * 30,
    body: {
      value,
      activitySessionId,
      questionSlug,
    },
  });
}

export async function updateOnboardingAnswer(
  token: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { trackId, surveyStep }: { trackId: number; surveyStep: Record<string, any> }
): Promise<void> {
  await axios({
    method: 'post',
    url: `/camp/onboarding/`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      trackId,
      surveyStep,
    },
  });
  return;
}

export async function updateOnboardingSlackJoin(
  token: string,
  { trackId, didJoinSlack }: { trackId: number; didJoinSlack: boolean }
): Promise<void> {
  await axios({
    method: 'post',
    url: `/camp/onboarding/`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      trackId,
      didJoinSlack,
    },
  });
  return;
}
export async function getProjectExamples(
  token: string
): Promise<GetProjectExamplesResponse> {
  return requestContract(getProjectExamplesRoute, { token });
}

export async function joinEnkiCommunity(
  token: string,
  { source }: { source: string }
): Promise<void> {
  await axios({
    method: 'post',
    url: `/community/invite?source=${source}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return;
}
export async function checkEmailAvailability({
  email,
}: {
  email: string;
}): Promise<{ isAvailable: boolean }> {
  return requestContract(checkProsumerEmailAvailabilityRoute, {
    params: { email },
  });
}

export type StaticLearningPlanUnit = {
  status: 'not-started' | 'in-progress' | 'completed';
  reasoning: string;
  slug: string;
} & (
  | {
      type: 'project';
      project: {
        slug: string;
        name: string;
        description: string;
        imageUrl: string;
        topic: string;
        questionCount: number;
        tags: AppliedProjectTag[];
      };
    }
  | {
      type: 'content-module';
      content: {
        id: number;
        title: string;
        slug: string;
        imageUrl: string;
        description: string;
        topic: string;
        lessonsCount: number;
      };
    }
);

export type StaticPlanExplanationResponse = {
  summary: string;
  milestones: string[];
};

export type SaveOnboardingPayload = {
  success: boolean;
  hasPartnerCode: boolean;
  partnerCode: string | null;
  partnerWelcomeMessage: string | null;
  shouldSkipPayment: boolean;
  isAppUser: boolean;
  learningPlan: StaticLearningPlanUnit[] | null;
  planExplanation: {
    summary: string;
    milestones: string[];
  } | null;
};

export async function saveSoloOnboarding({
  onboardingSteps,
  email,
  partnerSignupCode,
  projectSlug,
}: {
  onboardingSteps: OnboardQuestion[];
  email: string;
  partnerSignupCode: string | null;
  projectSlug: string | null;
}): Promise<SaveOnboardingPayload> {
  const response = await axios<null, SaveOnboardingPayload>({
    method: 'post',
    url: `/prosumer/onboarding/save`,
    data: {
      onboardingSteps,
      email,
      partnerSignupCode,
      projectSlug,
    },
  });
  return response.data;
}

export async function finishSoloOnboarding(token: string): Promise<{
  partnerSignupCode: string;
  partnerWelcomeMessage: string;
  hasPartnerCode: boolean;
}> {
  const response = await axios<
    null,
    {
      partnerSignupCode: string;
      partnerWelcomeMessage: string;
      hasPartnerCode: boolean;
    }
  >({
    method: 'post',
    url: `/prosumer/onboarding/finish`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
}

export async function getProsumerProfile(
  token: string
): Promise<ProsumerProfilePayload> {
  const response = await axios<null, ProsumerProfilePayload>({
    method: 'get',
    url: `/prosumer/profile`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    withCredentials: true,
  });
  return response.data;
}

export async function joinProsumerContent(
  token: string,
  { contentId }: { contentId: number }
): Promise<{
  next_mission_slug: string;
  next_activity_slug: string;
}> {
  return requestContract(joinProsumerContentContractRoute, {
    token,
    body: { contentId },
  });
}

export async function createProsumerLearnModePaymentIntent({
  userEmail,
  isMonthlySubscription,
}: {
  userEmail: string;
  isMonthlySubscription: boolean;
}): Promise<{
  paymentIntentClientSecret: string;
  discountPercentage?: number;
}> {
  const response = await axios<
    null,
    { paymentIntentClientSecret: string; discountPercentage?: number }
  >({
    method: 'post',
    url: `prosumer/learn-mode/payment-setup`,
    data: {
      userEmail,
      isMonthlySubscription,
    },
    withCredentials: true,
  });

  return response.data;
}

export async function postTeamsInterestForm({
  workEmail,
  company,
  teamSize,
  extraInfo,
}: {
  workEmail: string;
  company: string;
  teamSize: string;
  extraInfo: string;
}): Promise<void> {
  await requestContract(postTeamsInterestFormRoute, {
    body: {
      workEmail,
      company,
      teamSize,
      extraInfo,
    },
  });
}

type CreateLearnModeSubscriptionPayload = {
  email: string;
  isTrial: boolean;
  value: number;
  currency: string;
  metaEventId: string;
};

export async function createProsumerLearnModeSubscription({
  paymentIntentId,
}: {
  paymentIntentId: string;
}): Promise<CreateLearnModeSubscriptionPayload> {
  const response = await axios<null, CreateLearnModeSubscriptionPayload>({
    method: 'post',
    url: `prosumer/learn-mode/create-subscription`,
    data: {
      paymentIntentId,
    },
    withCredentials: true,
  });
  return response.data;
}

type GetProsumerUpsellDataResponse = {
  success: boolean;
  learningPlan: StaticLearningPlanUnit[] | null;
  planExplanation: StaticPlanExplanationResponse | null;
};

export async function getLearningPlanUpsell(
  token: string
): Promise<GetProsumerUpsellDataResponse> {
  const response = await axios<null, GetProsumerUpsellDataResponse>({
    method: 'get',
    url: `/prosumer/upsell`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

type GetProsumerUpsellDataPublicResponse = {
  user: UserPayload | null;
  learningPlan: StaticLearningPlanUnit[] | null;
  planExplanation: StaticPlanExplanationResponse | null;
  shouldGetTrial: boolean;
  hasSubscription: boolean;
};

export async function getProsumerPublicUpsellData({
  emailToken,
}: {
  emailToken: string;
}): Promise<GetProsumerUpsellDataPublicResponse> {
  const response = await axios<null, GetProsumerUpsellDataPublicResponse>({
    method: 'get',
    url: `/prosumer/upsell-public?emailToken=${emailToken}`,
  });
  return response.data;
}
export async function createProsumerAIReview(
  token: string,
  {
    activitySlug,
    submissionId,
    questionSlugToAnswer,
  }: {
    activitySlug: string;
    submissionId: number;
    questionSlugToAnswer: Record<string, ActivityContextQuestionAnswer>;
  }
): Promise<void> {
  await requestContract(createProsumerAIReviewRoute, {
    token,
    body: {
      activitySlug,
      submissionId,
      questionSlugToAnswer,
    },
  });
}

export async function createTeamsAIReview(
  token: string,
  {
    activitySlug,
    submissionId,
    questionSlugToAnswer,
  }: {
    activitySlug: string;
    submissionId: number;
    questionSlugToAnswer: Record<string, ActivityContextQuestionAnswer>;
  }
): Promise<void> {
  await requestContract(createTeamsAIReviewRoute, {
    token,
    body: {
      activitySlug,
      submissionId,
      questionSlugToAnswer,
    },
  });
}
export async function unsubscribeProsumerPreferences({
  emailToken,
}: {
  emailToken: string;
}): Promise<void> {
  await requestContract(unsubscribeProsumerRoute, {
    body: { emailToken },
  });
}

export async function healthCheck(): Promise<void> {
  await requestContract(getHealthcheckRoute, {});
}
